import type React from 'react';
import {useState} from 'react';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography} from '@mui/material';
import type {CategoryPlan} from '../../../types/strategyTypes';

type Props = {plan: CategoryPlan; onSubmit: (note: string) => Promise<unknown>; onClose: () => void};

/** Submit for sign-off: lists the approvers, flags unmet quality criteria, takes a note. Mount it to open it. */
export const SubmitDialog: React.FC<Props> = ({plan, onSubmit, onClose}) => {
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const unmet = plan.derived.quality.filter(c => !c.met && c.key !== 'consensus');

    const submit = () => {
        setBusy(true);
        onSubmit(note).then(onClose).catch(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cp-submit-title">
            <DialogTitle id="cp-submit-title">{plan.status === 'returned' ? 'Resubmit for sign-off' : 'Submit for sign-off'}</DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    <div>
                        <Typography variant="subtitle2">Approvers</Typography>
                        {plan.approvers.map(a => <Typography key={a.slot} variant="body2">{a.label}</Typography>)}
                        <Typography variant="caption" color="text.secondary">
                            Approvers are super users other than you; each signs for one role.
                        </Typography>
                    </div>
                    {unmet.length > 0 && (
                        <Alert severity="warning">
                            {`${unmet.length} quality ${unmet.length === 1 ? 'criterion is' : 'criteria are'} not met: `}
                            {unmet.map(c => c.label).join(', ')}. You can still submit; the approvers will see this.
                        </Alert>
                    )}
                    <TextField label="Note for the approvers" multiline minRows={3} value={note} onChange={e => setNote(e.target.value)}
                               slotProps={{htmlInput: {maxLength: 2000}}}/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy}>Submit</Button>
            </DialogActions>
        </Dialog>
    );
};
